import * as React from "react"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TablePagination from "@mui/material/TablePagination"
import TableRow from "@mui/material/TableRow"
import { createUseStyles, useTheme } from "react-jss"
import { useAppSelector } from "../../util/hooks"
import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { ddcDataSelector } from "./DataMetaDataSlice"
import { IDDCTableColumn, IDDCTableTableData } from "../../util/interface"
import { ABBREVIATE_NUMBER } from "../../util/CommonMethods"
import GaugeComponent from "react-gauge-component"
import { v4 as uuidv4 } from "uuid"
import { CustomErrorMsg } from "../../components/CustomErrorMsg"
import { ErrorBoundary } from "react-error-boundary"
const useStyles = createUseStyles({
  root: {
    "& .MuiTableCell-head": {
      backgroundColor: "#fbfbfb",
    },
  },
  borderRight: {
    borderRight: "1px solid lightgray",
  },
  borderBottom: {
    borderBottomColor: "transparent !important",
  },
  heading: {
    fontSize: 16,
    fontWeight: 600,
  },
  subHeading: {
    color: "#606060",
    fontSize: 14,
    fontWeight: 600,
  },
})

const colBorder = [0, 1, 2, 3, 4, 7]

const ResultDiv = styled.div`
  height: 25px;
  width: 70px;
  display: flex;
  padding: 2px 10px;
  border-radius: 5px;
  margin: 5px;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
`

const PassDiv = styled(ResultDiv)`
  background-color: #27af8d;
  color: white;
`
const WarnDiv = styled(ResultDiv)`
  background-color: rgb(246, 187, 66);
  color: white;
  width: 90px;
`

const FailedDiv = styled(ResultDiv)`
  background-color: #fcc;
  font-size: "14px";
  color: #da1818;
`

const columns: IDDCTableColumn[] = [
  {
    id: "TABLE_NAME",
    label: "Base Table Name",
    minWidth: 100,
    align: "left",
  },
  {
    id: "KPI",
    label: "KPI",
    minWidth: 200,
    align: "center",
  },
  {
    id: "MONTHLY_SUM",
    label: "Monthly Sum",
    minWidth: 270,
    align: "left",
    format: (value: number) => ABBREVIATE_NUMBER(value, 2),
  },
  {
    id: "LAST_12_MONTH_AVG",
    label: "Last 12 Months Average",
    minWidth: 200,
    align: "center",
    format: (value: number) => ABBREVIATE_NUMBER(value, 2),
  },
  {
    id: "PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG",
    label: "Drift %",
    width: 130,
    align: "center",
    format: (value: number) => value.toFixed(0) + " %",
  },
  {
    id: "RESULT",
    label: "Result",
    minWidth: 190,
    align: "center",
  },
]

function createData({
  TABLE_NAME,
  KPI,
  MONTHLY_SUM,
  LAST_12_MONTH_AVG,
  PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG,
  RESULT,
  HARD_THRESHOLD_LOWER_LIMIT,
  HARD_THRESHOLD_UPPER_LIMIT,
  SOFT_THRESHOLD_LOWER_LIMIT,
  SOFT_THRESHOLD_UPPER_LIMIT,
}: IDDCTableTableData): // TABLE_NAME: string,
// KPI: string,
// MONTHLY_SUM: number,
// LAST_12_MONTH_AVG: number,
// PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG: number,
// RESULT: string,
IDDCTableTableData {
  return {
    TABLE_NAME,
    KPI,
    MONTHLY_SUM,
    LAST_12_MONTH_AVG,
    PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG,
    RESULT,
    HARD_THRESHOLD_LOWER_LIMIT,
    HARD_THRESHOLD_UPPER_LIMIT,
    SOFT_THRESHOLD_LOWER_LIMIT,
    SOFT_THRESHOLD_UPPER_LIMIT,
  }
}

function Gauge({
  HARD_THRESHOLD_LOWER_LIMIT,
  HARD_THRESHOLD_UPPER_LIMIT,
  SOFT_THRESHOLD_LOWER_LIMIT,
  SOFT_THRESHOLD_UPPER_LIMIT,
  PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG,
}: any) {
  return (
    <ErrorBoundary fallback={<CustomErrorMsg />}>
      <GaugeComponent
        style={{ width: "220px" }}
        type="semicircle"
        arc={{
          width: 0.26,
          padding: 0.002,
          cornerRadius: 1,
          subArcs: [
            {
              limit: HARD_THRESHOLD_LOWER_LIMIT,
              color: "#EA4228", // Red
              showTick: true,
            },
            {
              limit: SOFT_THRESHOLD_LOWER_LIMIT,
              color: "#F5CD19", // Yellow
              showTick: true,
            },
            {
              limit: SOFT_THRESHOLD_UPPER_LIMIT,
              color: "#5BE12C", // Green
              showTick: true,
            },
            {
              limit: HARD_THRESHOLD_UPPER_LIMIT,
              color: "#F5CD19", // Yellow
              showTick: true,
            },
            {
              limit: HARD_THRESHOLD_UPPER_LIMIT + 10,
              color: "#EA4228", // Red
              showTick: false,
            },
          ],
        }}
        pointer={{
          color: "#E3E3E3",
          // color:"gray",
          length: 0.8,
          width: 15,
        }}
        labels={{
          valueLabel: {
            formatTextValue: (value) => value + "%",
            style: { fill: "black" },
          },
          tickLabels: {
            type: "outer",
            hideMinMax: true,
          },
        }}
        value={PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG}
        minValue={HARD_THRESHOLD_LOWER_LIMIT - 15}
        maxValue={HARD_THRESHOLD_UPPER_LIMIT + 15}
      />
    </ErrorBoundary>
  )
}

const TableHeader: React.FC<{ classes: any }> = ({ classes }) => (
  <TableHead className={classes.root}>
    <TableRow>
      <TableCell key={"id"} align={"center"} className={classes.borderRight}>
        <Typography variant="tableHeader"> S.No </Typography>
      </TableCell>
      {columns.map((column, i) => (
        <TableCell
          key={uuidv4()}
          align={column.align}
          sx={{
            height: "80px",
            borderRight: colBorder.includes(i) ? "1px solid lightgray" : "",
          }}
        >
          <Typography variant="tableHeader"> {column.label} </Typography>
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
)

const formatCellValue = (column: any, value: any, classes: any) => {
  if (column.id === "MATCH_PERCENTAGE") {
    return (
      <div className={classes.matchPercentage}>
        {column.format && typeof value === "number"
          ? column.format(value)
          : value}
      </div>
    )
  }
  if (column.id === "ACCEPTABILITY_CRITERIA") {
    return (
      <div className={classes.acceptabilityCriteria}>
        {column.format && typeof value === "number"
          ? column.format(value)
          : value}
      </div>
    )
  }
  if (column.format && typeof value === "number") {
    return column.format(value)
  }
  if (value === "PASS") {
    return <PassDiv>Pass</PassDiv>
  }
  if (value === "FAIL") {
    return <FailedDiv>Failed</FailedDiv>
  }
  if (value === "WARNING") {
    return <WarnDiv>Warning</WarnDiv>
  }
  return value
}

const TableCellComponent = ({ column, value, classes, i }: any) => (
  <TableCell
    key={uuidv4()}
    align={column.align}
    sx={{
      borderRight: colBorder.includes(i) ? "1px solid lightgray" : "",
      width: column.width,
    }}
  >
    {formatCellValue(column, value, classes)}
  </TableCell>
)

const TableRowComponent = ({ row, index, page, rowsPerPage, classes }: any) => (
  <TableRow hover role="checkbox" tabIndex={-1} key={uuidv4()}>
    <TableCell
      align={"left"}
      sx={{ borderRight: "1px solid lightgray", minWidth: 2 }}
    >
      {page * rowsPerPage + index + 1}
    </TableCell>
    {columns.map((column, i) =>
      column.id !== "PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG" ? (
        <TableCellComponent
          key={uuidv4()}
          column={column}
          value={row[column.id]}
          classes={classes}
          i={i}
        />
      ) : (
        <TableCell
          key={uuidv4()}
          align={column.align}
          sx={{
            borderRight: colBorder.includes(i) ? "1px solid lightgray" : "",
            width: column.width,
          }}
        >
          <Gauge {...row} />
        </TableCell>
      ),
    )}
  </TableRow>
)

const TableRows: React.FC<{
  rows: IDDCTableTableData[]
  page: number
  rowsPerPage: number
  classes: any
}> = ({ rows, page, rowsPerPage, classes }) => (
  <TableBody>
    {rows
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((row, index) => (
        <TableRowComponent
          key={uuidv4()}
          row={row}
          index={index}
          page={page}
          rowsPerPage={rowsPerPage}
          classes={classes}
        />
      ))}
  </TableBody>
)

export default function DataDriftCheckTable() {
  const [page, setPage] = React.useState(0) // Current page in pagination
  const [rowsPerPage, setRowsPerPage] = React.useState(10) // Rows to display per page
  const theme: any = useTheme()
  const classes = useStyles({ theme })

  const data: IDDCTableTableData[] = useAppSelector(ddcDataSelector)
  const [rows, setRows] = React.useState<IDDCTableTableData[]>([]) // State to hold transformed rows

  // Transform incoming data into the format required for the table
  React.useEffect(() => {
    const temp: IDDCTableTableData[] = data.map((row) =>
      createData({
        TABLE_NAME: row["TABLE_NAME"],
        KPI: row["KPI"],
        LAST_12_MONTH_AVG: row["LAST_12_MONTH_AVG"],
        MONTHLY_SUM: row["MONTHLY_SUM"],
        PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG:
          row["PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG"],
        RESULT: row["RESULT"],
        HARD_THRESHOLD_LOWER_LIMIT: row["HARD_THRESHOLD_LOWER_LIMIT"],
        HARD_THRESHOLD_UPPER_LIMIT: row["HARD_THRESHOLD_UPPER_LIMIT"],
        SOFT_THRESHOLD_LOWER_LIMIT: row["SOFT_THRESHOLD_LOWER_LIMIT"],
        SOFT_THRESHOLD_UPPER_LIMIT: row["SOFT_THRESHOLD_UPPER_LIMIT"],
      }),
    )
    setRows(temp) // Update the rows state with transformed data
  }, [data])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value) // Update rows per page
    setPage(0) // Reset to the first page
  }

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 515 }}>
        <Table stickyHeader aria-label="sticky table">
          {/* Render table header and rows */}
          <TableHeader classes={classes} />
          <TableRows
            rows={rows}
            page={page}
            rowsPerPage={rowsPerPage}
            classes={classes}
          />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20]}
        component="div"
        count={rows.length} // Total number of rows
        rowsPerPage={rowsPerPage} // Current number of rows displayed
        page={page} // Current page
        onPageChange={handleChangePage} // Function to handle page changes
        onRowsPerPageChange={handleChangeRowsPerPage} // Function to handle changing the number of rows per page
      />
    </Paper>
  )
}
